/**
 * Merchant balance-query list (PR-6a — operational observability).
 *
 * Today's only producer is M-Pesa Daraja's AccountBalance API. State
 * is derived: dispatched / stale / settled / failed.
 */
import { useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Wallet, RotateCw } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NameAvatar } from "@/components/shell/NameAvatar";
import { ListPagination } from "@/components/shell/ListPagination";
import { useEnvironment } from "@/lib/environment-context";
import { isMissingPage, LIST_PAGE_SIZE, usePageParam } from "@/lib/use-page-param";
import { CopyId } from "@/components/shell/CopyId";
import { RelativeTime } from "@/components/shell/RelativeTime";
import { formatMethod } from "@/lib/format";
import { DataTable, type DataTableColumn } from "@/components/shell/DataTable";
import { EmptyState } from "@/components/shell/EmptyState";
import { FilterChip } from "@/components/shell/FilterChip";
import { PageHeader } from "@/components/shell/PageHeader";
import { Toolbar, FilterGroup } from "@/components/shell/Toolbar";
import {
  getBalanceQueries,
  type BalanceQueryRow,
  type BalanceQueryState,
} from "@/lib/api";
import { useAuth } from "@/lib/use-auth";

const STATES: readonly BalanceQueryState[] = [
  "dispatched",
  "settled",
  "failed",
  "stale",
] as const;

const STATE_VARIANT: Record<BalanceQueryState, BadgeProps["variant"]> = {
  dispatched: "info",
  settled: "success",
  failed: "destructive",
  stale: "warning",
};

export function BalanceQueriesListPage() {
  const [params, setParams] = useSearchParams();
  const { page, setPage } = usePageParam();
  const { environment: env } = useEnvironment();
  const navigate = useNavigate();
  const { user } = useAuth();

  const state = (params.get("state") as BalanceQueryState | null) ?? undefined;

  const query = useQuery({
    queryKey: ["balance-queries", { env, state, page }] as const,
    queryFn: () => getBalanceQueries({ environment: env, state, page, page_size: LIST_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.error && !isMissingPage(query.error, page)) {
      toast.error("Couldn't load balance queries", {
        description: "Check your network and try again.",
      });
    }
  }, [query.error, page]);

  function setFilter(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  const hasFilters = Boolean(state);
  const isZeroGrantTenant =
    user?.role === "tenant" && user.tenants.length === 0 && !hasFilters;

  const columns = useMemo<ReadonlyArray<DataTableColumn<BalanceQueryRow>>>(
    () => [
      {
        key: "id",
        header: "Query",
        cell: (r) => (
          <CopyId id={r.id} />
        ),
      },
      {
        key: "tenant",
        header: "Tenant",
        cell: (r) => (
          <div className="flex min-w-0 items-center gap-2">
            <NameAvatar name={r.tenant.name} />
            <span className="truncate font-medium">{r.tenant.name}</span>
            {r.tenant.environment === "test" ? <Badge variant="muted">Test</Badge> : null}
          </div>
        ),
      },
      {
        key: "account",
        header: "Provider account",
        cell: (r) => (
          <span className="text-sm">
            {formatMethod(r.provider_account.plugin)} · {r.provider_account.name}
          </span>
        ),
      },
      {
        key: "state",
        header: "State",
        cell: (r) => (
          <Badge dot variant={STATE_VARIANT[r.state]} className="capitalize">
            {r.state}
          </Badge>
        ),
      },
      {
        key: "result_code",
        header: "Result",
        className: "tabular-nums",
        cell: (r) => (
          <span className="text-sm">{r.result_code ?? "—"}</span>
        ),
      },
      {
        key: "created_at",
        header: "Dispatched",
        className: "text-muted-foreground",
        cell: (r) => (
          <RelativeTime iso={r.created_at} />
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        icon={Wallet}
        title="Balance queries"
        description="Outstanding and settled merchant-balance queries. M-Pesa Daraja is the only producer today."
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RotateCw className={query.isFetching ? "animate-spin" : undefined} />
            Refresh
          </Button>
        }
      />

      <Toolbar
        filters={
          <FilterGroup label="State">
            <FilterChip
              active={state === undefined}
              onClick={() => setFilter("state", null)}
            >
              All
            </FilterChip>
            {STATES.map((s) => (
              <FilterChip
                key={s}
                active={state === s}
                onClick={() => setFilter("state", s)}
              >
                {s}
              </FilterChip>
            ))}
          </FilterGroup>
        }
      />

      <DataTable<BalanceQueryRow>
        stale={query.isPlaceholderData}
        columns={columns}
        rows={query.data?.results ?? []}
        loading={query.isLoading}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/balance-queries/${r.id}`)}
        emptyState={
          isZeroGrantTenant ? (
            <EmptyState
              icon={Wallet}
              title="No tenants granted yet"
              description="Balance queries appear here once a super-admin grants you access to a tenant."
            />
          ) : (
            <EmptyState
              icon={Wallet}
              title="No balance queries match these filters"
              description="Try clearing a filter. Queries appear here as your platform dispatches AccountBalance requests."
              action={
                hasFilters ? (
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/balance-queries">Clear filters</Link>
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />

      <ListPagination query={query} page={page} onPage={setPage} />
    </>
  );
}

export default BalanceQueriesListPage;
